import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import loadWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/LoadWishList";
import { getWishListState } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import reloadPageIfAuthenticated from "@insite/client-framework/Store/Pages/SignIn/Handlers/ReloadPageIfAuthenticated";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Page from "@insite/mobius/Page";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { breakpointsForMediaQuery } from "@sonic/CustomComponents/headless/utils/Breakpoints";
import GridContainer from "@sonic/Overrides/Components/GridContainer";
import LoadingOverlay from "@sonic/Overrides/Components/LoadingOverlay";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    image = "image",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.image]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const {
        pages: {
            signIn: { isSigningInAsGuest },
        },
        context: { isSigningIn },
    } = state;
    const queryParams = parseQueryString<{ returnUrl?: string; clientRedirect?: string }>(getLocation(state).search);
    const lowerCaseReturnUrl = queryParams.returnUrl?.toLowerCase();
    const myListsDetailsPageUrl = getPageLinkByPageType(state, "MyListsDetailsPage")?.url;
    const staticListPageUrl = getPageLinkByPageType(state, "StaticListPage")?.url;

    let invitedToList = false;
    if (
        lowerCaseReturnUrl &&
        myListsDetailsPageUrl &&
        lowerCaseReturnUrl.indexOf(myListsDetailsPageUrl.toLowerCase()) > -1 &&
        lowerCaseReturnUrl.indexOf("invite=") > -1
    ) {
        invitedToList = true;
    }

    let staticListId;
    let navigatedFromStaticList = false;
    const idParam = "?id=";
    if (
        lowerCaseReturnUrl &&
        staticListPageUrl &&
        lowerCaseReturnUrl.indexOf(staticListPageUrl.toLowerCase()) > -1 &&
        lowerCaseReturnUrl.indexOf(idParam) > -1 &&
        !queryParams.clientRedirect
    ) {
        const start = lowerCaseReturnUrl.indexOf(idParam) + idParam.length;
        staticListId = lowerCaseReturnUrl.substring(start, start + 36);
        navigatedFromStaticList = true;
    }

    const staticListState = getWishListState(state, staticListId);
    return {
        // This means the loading overlay shows until the browser redirects
        showLoadingOverlay: isSigningIn || isSigningInAsGuest || staticListState.isLoading,
        invitedToList,
        navigatedFromStaticList,
        staticListId,
        staticListState,
    };
};

const mapDispatchToProps = {
    reloadPageIfAuthenticated,
    loadWishList,
};

type Props = OwnProps &
    PageProps &
    ResolveThunks<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps> &
    HasShellContext;

export interface SignInPageStyles {
    imageGridItem: GridItemProps;
    signInGridItem: GridItemProps;
    modal?: ModalPresentationProps;
    innerWrapper?: InjectableCss;
    messageText?: TypographyPresentationProps;
    buttonsWrapper?: InjectableCss;
    closeButton?: ButtonPresentationProps;
}

export const signInPageStyles: SignInPageStyles = {
    imageGridItem: {
        css: css`
            @media ${breakpointsForMediaQuery.mdDown} {
                display: none;
            }
        `,
    },
    signInGridItem: {
        css: css`
            margin-left: 0;
            margin-top: 24px;
            padding: 24px 32px;
            padding-bottom: 40px;

            @media ${breakpointsForMediaQuery.md} {
                margin-left: auto;
                padding-left: 80px;
            }

            @media ${breakpointsForMediaQuery.lg} {
                padding-left: 133px;
            }

            h1 {
                font-size: 48px;
                line-height: 1;
                padding: 0;
                margin-bottom: 20px;
                max-width: 80%;
                word-break: break-word;
                font-style: normal;
                font-weight: normal;
            }
        `,
    },
    modal: {
        size: 500,
        cssOverrides: {
            modalTitle: css`
                padding: 10px 20px;
            `,
            modalContent: css`
                padding: 20px;
            `,
        },
    },
    buttonsWrapper: {
        css: css`
            margin-top: 30px;
            text-align: right;
        `,
    },
    closeButton: {
        css: css`
            margin-left: 10px;
        `,
    },
};

const SvgElement = styled.svg`
    position: absolute;
    top: 0;
    right: -2px;
    height: 100%;
`;

const styles = signInPageStyles;

const SignInPage = ({
    id,
    showLoadingOverlay,
    invitedToList,
    navigatedFromStaticList,
    staticListId,
    staticListState,
    reloadPageIfAuthenticated,
    loadWishList,
    shellContext: { isInShell },
    fields,
}: Props) => {
    const [signInRequiredModalIsOpen, setSignInRequiredModalIsOpen] = useState(false);

    useEffect(() => {
        if (!showLoadingOverlay && !isInShell) {
            reloadPageIfAuthenticated();
        }
    });

    useEffect(() => {
        if (invitedToList) {
            setSignInRequiredModalIsOpen(true);
        }
    }, [invitedToList]);

    useEffect(() => {
        if (navigatedFromStaticList && staticListId) {
            loadWishList({ wishListId: staticListId, expand: ["staticList"], exclude: ["listLines"] });
        }
    }, [navigatedFromStaticList]);

    useEffect(() => {
        if (!staticListState.isLoading && staticListState.value) {
            setSignInRequiredModalIsOpen(true);
        }
    }, [staticListState]);

    const signInRequiredModalCloseHandler = () => {
        setSignInRequiredModalIsOpen(false);
    };

    const backGroundStyles = {
        css: css`
            width: 100%;
            height: 100%;
            background-size: cover;
            background-repeat: no-repeat;
            background-position: right top;
            z-index: -1;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        background-image: none;
                    `,
                    css`
                        background-image: none;
                    `,
                    css`
                        background-image: none;
                    `,
                    css`
                        background-image: url(${fields.image});
                    `,
                    css`
                        background-image: url(${fields.image});
                    `,
                ])}
        `,
    };

    return (
        <LoadingOverlay style={{ display: "flex", flex: 1, flexDirection: "column" }} loading={showLoadingOverlay}>
            <Page
                data-test-selector="signIn"
                style={{
                    background: "transparent",
                    maxWidth: "100%",
                    padding: 0,
                    display: "flex",
                    flex: 1,
                    flexDirection: "column",
                }}
                className="signin"
            >
                <GridContainer
                    style={{ display: "flex", flex: 1, flexDirection: "column" }}
                    offsetCss={css`
                        display: flex;
                        flex: 1;
                    `}
                    gap={0}
                >
                    <GridItem
                        {...styles.imageGridItem}
                        width={[12, 12, 6, 6, 8]}
                        style={{ position: "relative" }}
                        className="column-left"
                    >
                        <StyledWrapper {...backGroundStyles} />
                        <SvgElement xmlns="http://www.w3.org/2000/svg" viewBox="0 0 191 403">
                            <path
                                fill="#FFF"
                                d="M0.25,0 L190.25,0 L190.25,403 L117.935295,403 L182.535295,190.7 C192.035295,159.5 180.435295,125.8 153.835295,107.2 L0.25,0 Z"
                            />
                        </SvgElement>
                    </GridItem>
                    <GridItem
                        {...styles.signInGridItem}
                        width={[12, 12, 12, 6, 4]}
                        style={{ flexWrap: "wrap", gap: 0, height: "max-content" }}
                        className="column-right"
                    >
                        <Zone contentId={id} zoneName="Page Title" />
                        <Zone contentId={id} zoneName="sign in" />
                        <Zone contentId={id} zoneName="new acc" />
                    </GridItem>
                </GridContainer>
            </Page>
            <Modal
                headline={translate("Please Sign In or Create an Account")}
                isOpen={signInRequiredModalIsOpen}
                handleClose={signInRequiredModalCloseHandler}
            >
                <StyledWrapper {...styles.innerWrapper}>
                    {invitedToList && (
                        <Typography {...styles.messageText}>
                            {siteMessage("Lists_SignIn_Required_To_Join_List")}
                        </Typography>
                    )}
                    {navigatedFromStaticList && staticListState.value && (
                        <Typography {...styles.messageText}>
                            {translate("In order to view the list shared by")}
                            {` ${staticListState.value.sharedByDisplayName}, `}
                            {translate("please sign in or create an account.")}
                        </Typography>
                    )}
                    <StyledWrapper {...styles.buttonsWrapper}>
                        <Button {...styles.closeButton} onClick={signInRequiredModalCloseHandler}>
                            {translate("Close")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </Modal>
        </LoadingOverlay>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(SignInPage)),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
        fieldDefinitions: [
            {
                name: fields.image,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
            },
        ],
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "SignInPage" instead of this constant.
 */
export const SignInPageContext = "SignInPage";
